import PropTypes from 'prop-types';
import { PostToBePublishedShape } from "../PostToBePublished";
import {FORM_MODE, FORM_NAME} from "../../../socialConstants";

export const AspectRatioShape = PropTypes.shape({
  min: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }),
  max: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }),
  dimensionRequirement: PropTypes.shape({
    minHeight: PropTypes.number,
    maxHeight: PropTypes.number,
    minWidth: PropTypes.number,
    maxWidth: PropTypes.number
  })
});

/**
  * The Shape of the Config for Editing a Post
  *
  * Config Options for Immediate Reply
  * @param replyWord - the word associated with the immediate reply type
  * @param canPublishImmediateReply - whether a user can publish a reply/comment immediately
  * @param requireImmediateReply - whether an immediately reply/comment required in order to publish
  * @param minReplyLength - the minimum number of characters a reply must be in order to be valid
  * @param maxReplyLength - the maximum number of characters a reply can be in order to be valid
  *
  * Look and Feel Config
  * @param textPlaceholder - the default text to display to a user when they haven't started creating a new post.
  *
  * Main Post Requirements
  * @param minLength - the minimum number of characters that a post description must include
  * @param maxLength - the maximum number of characters that a post description can include
  * @param requireDescription - whether or not a post description is required
  *
  * Attachment Config
  * @param maxAttachments - the maximum number of attachments that a user can attach. This will superseed any individual
  *                         requirements for images or videos.
  * @param disableAttachments - if the provider doesn't support attachments, you can disable them from getting added.
  * @param canAttachImages - whether or not images can be attached. By default, this is true
  * @param canAttachVideos - whether or not videos can be attached. By default, this is false
  * @param requireAttachment - whether or not an attachment is required.
  * @param canHaveMixedMedia - whether or not you can have multiple types of attachments on a post.
  * @param minVideoDuration - the minimum length a video must be in order to be considered valid, in seconds
  * @param maxVideoDuration - the maximum length a video must be within in order to be considered valid, in seconds
  * @param maxNumberOfImages - the maximum number of images that you can attach
  * @param maxNumberOfVideos - the maximum number of videos that you can attach
  * @param maxNumberOfLinks - the maximum number of links that a user can attach.
  * @param aspectRatio - the aspect ratio requirements for an image
  * @param videoAspectRatio - the aspect ratio requirements for a video
  * @param maxNumberOfHashtags - the maximum number of hashtags that can be included in a post
  */
const DefaultConfigShape = PropTypes.shape({
  /* Config Options for Immediate Reply */
  replyWord: PropTypes.string,
  canPublishImmediateReply: PropTypes.bool,
  requireImmediateReply: PropTypes.bool,
  minReplyLength: PropTypes.number,
  maxReplyLength: PropTypes.number,
  /* Look and Feel Config */
  textPlaceholder: PropTypes.string,
  /* Main Post Requirements */
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  requireDescription: PropTypes.bool,
  /* Attachment Config */
  maxAttachments: PropTypes.number.isRequired,
  disableAttachments: PropTypes.bool,
  canAttachImages: PropTypes.bool,
  canAttachVideos: PropTypes.bool,
  requireAttachment: PropTypes.bool,
  canHaveMixedMedia: PropTypes.bool,
  minVideoDuration: PropTypes.number,
  maxVideoDuration: PropTypes.number,
  maxNumberOfImages: PropTypes.number,
  maxNumberOfVideos: PropTypes.number,
  maxNumberOfLinks: PropTypes.number.isRequired,
  aspectRatio: AspectRatioShape,
  videoAspectRatio: AspectRatioShape,
  maxNumberOfHashtags: PropTypes.number
});

export const FacebookConfigShape = DefaultConfigShape;
export const InstagramConfigShape = DefaultConfigShape;
export const TwitterConfigShape = DefaultConfigShape;
export const LinkedInConfigShape = DefaultConfigShape;

/**
  * The Shape of the Config for Editing a Google Post
  *
  * @param replyWord - the word associated with the immediate reply type. Used for Google Q&A.
  * @param canPublishImmediateReply - whether a user can publish a reply/comment immediately. Used for Google Q&A.
  * @param requireImmediateReply - whether an immediately reply/comment required in order to publish. Used for Google Q&A.
  * @param minReplyLength - the minimum number of characters a reply must be in order to be valid. Used for Google Q&A.
  * @param maxReplyLength - the maximum number of characters a reply can be in order to be valid. Used for Google Q&A.
  * @param textPlaceholder - the default text to display to a user when they haven't started creating a new post.
  * @param minLength - the minimum number of characters that a post description must include.
  * @param maxLength - the maximum number of characters that a post description can include.
  * @param minSummaryLength - the minimum number of characters that a post summary must include.
  * @param maxSummaryLength - the maximum number of characters that a post summary must include.
  * @param minEventTitleLength - the minimum number of characters that an event title must include.
  * @param maxEventTitleLength - the maximum number of characters that an event title must include.
  * @param minCouponLength - the minimum number of characters that a coupon must include.
  * @param maxCouponLength - the maximum number of characters that a coupon must include.
  * @param requireValidDateRanges - whether to enforce or not that an end date must be after a start date.
  * @param maxAttachments - the maximum number of attachments that a user can attach. This will supersede any individual requirements for images or videos.
  * @param disableAttachments - if the provider doesn't support attachments, you can disable them from getting added.
  * @param canAttachImages - whether or not images can be attached. By default, this is true.
  * @param maxNumberOfImages - the maximum number of images that you can attach.
  * @param maxNumberOfVideos - the maximum number of videos that you can attach.
  * @param maxNumberOfLinks - the maximum number of links that a user can attach.
  * @param requireValidUrl - If the user adds a Call-To-Action, whether or not the URL specified should be valid or not.
  */
export const GoogleConfigShape = PropTypes.shape({
  replyWord: PropTypes.string,
  canPublishImmediateReply: PropTypes.bool,
  requireImmediateReply: PropTypes.bool,
  minReplyLength: PropTypes.number,
  maxReplyLength: PropTypes.number,
  textPlaceholder: PropTypes.string,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  minSummaryLength: PropTypes.number,
  maxSummaryLength: PropTypes.number,
  minEventTitleLength: PropTypes.number,
  maxEventTitleLength: PropTypes.number,
  minCouponLength: PropTypes.number,
  maxCouponLength: PropTypes.number,
  requireValidDateRanges: PropTypes.bool,
  maxAttachments: PropTypes.number.isRequired,
  disableAttachments: PropTypes.bool,
  canAttachImages: PropTypes.bool,
  maxNumberOfImages: PropTypes.number, // Google says you can upload up to 10 images, but when you do it via API it errors out if > 1
  maxNumberOfVideos: PropTypes.number,
  maxNumberOfLinks: PropTypes.number,
  requireValidUrl: PropTypes.bool
});

/**
  * The shape of props required to all forms that will be used by the [[PostEditor]]
  *
  * @param formType - the name of the form that we're editing.
  * @param config - the provider specific config. Should you need to access any fields, such as if there should be an immediate reply, etc
  * @param provider - the provider that the post is for.
  * @param showPublishingTeaser - whether or not to disable form fields. If true, all fields should be disabled on the form
  * @param values - the current values that Formik has on record for the form
  * @param onChangeSetObject - When a value changes, override it with the incoming value (Js value). i.e. input {key: value} => set values[key] = value
  *                            this is extremely helpful when you need to merge in existing values with the current.
  * @param onChange - when using regular event handling for inputs, simply use this, and formik will take care of store the correct js value for you.
  * @param onBlur - when a user leaves a field, onBlur should be called in order to correctly perform a re-validation. Remember to ensure that the field must have name specified.
  * @param touched - an array of fields that have been touched.
  * @param errors - an object of errors where the key corresponds to the field that has the errors tied to it. The value is an array of errors. If the array is empty, assume no errors.
  * @param openModal - a function used to open modals, should you need it.
  * @param closeModal - a function used to close modals, should you need it.
  */
export const PostEditorFormFields =
  PropTypes.shape(
    {
      formType: PropTypes.oneOf([
        FORM_NAME.FacebookForm,
        FORM_NAME.InstagramForm,
        FORM_NAME.TwitterForm,
        FORM_NAME.GoogleAlertForm,
        FORM_NAME.GoogleOfferForm,
        FORM_NAME.GoogleWhatsNewForm
      ]).isRequired,
      config: PropTypes.oneOf([FacebookConfigShape, InstagramConfigShape, TwitterConfigShape, GoogleConfigShape]).isRequired,
      provider: PropTypes.string.isRequired,
      showPublishingTeaser: PropTypes.bool,
      mode: PropTypes.oneOf([FORM_MODE.EDIT, FORM_MODE.VIEW]),

      /* Formik Specific */
      values: PostToBePublishedShape.isRequired,
      onChangeSetObject: PropTypes.func.isRequired,
      onBlur: PropTypes.func.isRequired,
      onChange: PropTypes.func.isRequired,
      touched: PropTypes.array.isRequired,
      errors: PropTypes.object.isRequired,

      openModal: PropTypes.func.isRequired,
      closeModal: PropTypes.func.isRequired
    }
  )
  .isRequired;
